import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { journalCategories } from "@/lib/api/journal-categories";
import { journalEntries } from "@/lib/api/journal-entries";
import type {
  JournalEntriesFilter,
  JournalEntryInput,
} from "@/lib/api/journal-entries";
import type {
  JournalCategory,
  JournalEntry,
  JournalEntryRevision,
} from "@/types/api";
import { useAuth } from "./useAuth";

const api = { ...journalCategories, ...journalEntries };

export type JournalEntriesStatus = "idle" | "loading" | "ready" | "error";

export interface JournalEntriesContextValue {
  status: JournalEntriesStatus;
  entries: readonly JournalEntry[];
  allEntries: readonly JournalEntry[];
  categories: readonly string[];
  managedCategories: readonly JournalCategory[];
  error: Error | null;
  nextCursor: string | null;
  reload: (filter?: JournalEntriesFilter) => Promise<readonly JournalEntry[]>;
  search: (filter: JournalEntriesFilter) => Promise<readonly JournalEntry[]>;
  searchAll: (filter: JournalEntriesFilter) => Promise<readonly JournalEntry[]>;
  loadMore: (filter?: JournalEntriesFilter) => Promise<void>;
  create: (input: JournalEntryInput) => Promise<JournalEntry>;
  update: (id: string, patch: JournalEntryInput) => Promise<JournalEntry>;
  listRevisions: (id: string) => Promise<readonly JournalEntryRevision[]>;
  restoreRevision: (
    entryId: string,
    revisionId: string,
  ) => Promise<JournalEntry>;
  remove: (id: string) => Promise<void>;
  createCategory: (name: string) => Promise<JournalCategory>;
  updateCategory: (id: string, name: string) => Promise<JournalCategory>;
  reorderCategories: (ids: string[]) => Promise<void>;
  removeCategory: (id: string) => Promise<void>;
}

const JournalEntriesContext = createContext<JournalEntriesContextValue>(null!);

export function useJournalEntries(): JournalEntriesContextValue {
  return useContext(JournalEntriesContext);
}

function useJournalEntriesProvider(
  deferInitialLoad: boolean,
): JournalEntriesContextValue {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<JournalEntriesStatus>("idle");
  const [entries, setEntries] = useState<readonly JournalEntry[]>([]);
  const [allEntries, setAllEntries] = useState<readonly JournalEntry[]>([]);
  const [categories, setCategories] = useState<readonly string[]>([]);
  const [managedCategories, setManagedCategories] = useState<
    readonly JournalCategory[]
  >([]);
  const [error, setError] = useState<Error | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const reloadSequenceRef = useRef(0);
  const removedEntryIdsRef = useRef(new Set<string>());

  const reload = useCallback((filter?: JournalEntriesFilter) => {
    const reloadSequence = ++reloadSequenceRef.current;
    setStatus("loading");
    setError(null);
    return Promise.all([
      api.listJournalEntries(filter),
      api.listJournalCategories(),
    ])
      .then(([response, categoryResponse]) => {
        if (reloadSequence !== reloadSequenceRef.current) return [];
        const currentEntries = response.journal_entries.filter(
          (entry) => !removedEntryIdsRef.current.has(entry.id),
        );
        setEntries(currentEntries);
        const hasActiveFilter =
          filter &&
          Object.values(filter).some(
            (value) =>
              value != null &&
              value !== "" &&
              (!Array.isArray(value) || value.length > 0),
          );
        if (!hasActiveFilter) setAllEntries(currentEntries);
        setCategories(
          categoryResponse.journal_categories.map((category) => category.name),
        );
        setManagedCategories(categoryResponse.journal_categories);
        setNextCursor(response.next_cursor);
        setStatus("ready");
        return currentEntries;
      })
      .catch((nextError: Error) => {
        if (reloadSequence !== reloadSequenceRef.current) return [];
        setError(nextError);
        setStatus("error");
        return [];
      });
  }, []);

  const loadMore = useCallback(
    async (filter?: JournalEntriesFilter) => {
      if (!nextCursor) return;
      const reloadSequence = reloadSequenceRef.current;
      const response = await api.listJournalEntries({
        ...filter,
        before: nextCursor,
      });
      if (reloadSequence !== reloadSequenceRef.current) return;
      setEntries((previous) => {
        const known = new Set(previous.map((entry) => entry.id));
        return [
          ...previous,
          ...response.journal_entries.filter(
            (entry) =>
              !known.has(entry.id) && !removedEntryIdsRef.current.has(entry.id),
          ),
        ];
      });
      setNextCursor(response.next_cursor);
    },
    [nextCursor],
  );

  const search = useCallback(async (filter: JournalEntriesFilter) => {
    const response = await api.listJournalEntries(filter);
    return response.journal_entries.filter(
      (entry) => !removedEntryIdsRef.current.has(entry.id),
    );
  }, []);

  const searchAll = useCallback(async (filter: JournalEntriesFilter) => {
    const found = new Map<string, JournalEntry>();
    const cursors = new Set<string>();
    let before: string | undefined;
    do {
      const response = await api.listJournalEntries({
        ...filter,
        limit: 200,
        before,
      });
      for (const entry of response.journal_entries) {
        if (!removedEntryIdsRef.current.has(entry.id))
          found.set(entry.id, entry);
      }
      before = response.next_cursor ?? undefined;
      if (before) {
        if (cursors.has(before))
          throw new Error("Journal pagination did not advance");
        cursors.add(before);
      }
    } while (before);
    return [...found.values()];
  }, []);

  useEffect(() => {
    if (authStatus !== "authenticated" || deferInitialLoad) return;
    queueMicrotask(() => void reload());
  }, [authStatus, deferInitialLoad, reload]);

  const create = useCallback(async (input: JournalEntryInput) => {
    const { journal_entry } = await api.createJournalEntry(input);
    removedEntryIdsRef.current.delete(journal_entry.id);
    setEntries((previous) => [journal_entry, ...previous]);
    setAllEntries((previous) => [
      journal_entry,
      ...previous.filter((entry) => entry.id !== journal_entry.id),
    ]);
    setCategories((previous) =>
      previous.includes(journal_entry.category)
        ? previous
        : [...previous, journal_entry.category].sort(),
    );
    return journal_entry;
  }, []);

  const update = useCallback(async (id: string, patch: JournalEntryInput) => {
    const { journal_entry } = await api.updateJournalEntry(id, patch);
    setEntries((previous) =>
      previous.map((entry) => (entry.id === id ? journal_entry : entry)),
    );
    setAllEntries((previous) =>
      previous.map((entry) => (entry.id === id ? journal_entry : entry)),
    );
    return journal_entry;
  }, []);

  const listRevisions = useCallback(async (id: string) => {
    const { journal_entry_revisions } = await api.listJournalEntryRevisions(id);
    return journal_entry_revisions;
  }, []);

  const restoreRevision = useCallback(
    async (entryId: string, revisionId: string) => {
      const { journal_entry } = await api.restoreJournalEntryRevision(
        entryId,
        revisionId,
      );
      setEntries((previous) =>
        previous.map((entry) => (entry.id === entryId ? journal_entry : entry)),
      );
      setAllEntries((previous) =>
        previous.map((entry) => (entry.id === entryId ? journal_entry : entry)),
      );
      return journal_entry;
    },
    [],
  );

  const remove = useCallback(async (id: string) => {
    removedEntryIdsRef.current.add(id);
    try {
      await api.deleteJournalEntry(id);
      setEntries((previous) => previous.filter((entry) => entry.id !== id));
      setAllEntries((previous) => previous.filter((entry) => entry.id !== id));
    } catch (nextError) {
      removedEntryIdsRef.current.delete(id);
      throw nextError;
    }
  }, []);

  const createCategory = useCallback(async (name: string) => {
    const { journal_category } = await api.createJournalCategory(name);
    setManagedCategories((previous) => [...previous, journal_category]);
    setCategories((previous) =>
      previous.includes(journal_category.name)
        ? previous
        : [...previous, journal_category.name],
    );
    return journal_category;
  }, []);

  const updateCategory = useCallback(
    async (id: string, name: string) => {
      const previousName =
        managedCategories.find((category) => category.id === id)?.name ?? "";
      const { journal_category } = await api.updateJournalCategory(id, {
        name,
      });
      setManagedCategories((previous) =>
        previous.map((category) =>
          category.id === id ? journal_category : category,
        ),
      );
      setCategories((previous) =>
        previous.map((category) =>
          category === previousName ? journal_category.name : category,
        ),
      );
      setEntries((previous) =>
        previous.map((entry) =>
          entry.category === previousName
            ? { ...entry, category: journal_category.name }
            : entry,
        ),
      );
      setAllEntries((previous) =>
        previous.map((entry) =>
          entry.category === previousName
            ? { ...entry, category: journal_category.name }
            : entry,
        ),
      );
      return journal_category;
    },
    [managedCategories],
  );

  const reorderCategories = useCallback(async (ids: string[]) => {
    const { journal_categories } = await api.reorderJournalCategories(ids);
    setManagedCategories(journal_categories);
    setCategories(journal_categories.map((category) => category.name));
  }, []);

  const removeCategory = useCallback(
    async (id: string) => {
      const removed = managedCategories.find((category) => category.id === id);
      await api.deleteJournalCategory(id);
      setManagedCategories((previous) =>
        previous.filter((category) => category.id !== id),
      );
      if (!removed) return;
      setCategories((previous) =>
        previous.filter((category) => category !== removed.name),
      );
      setEntries((previous) =>
        previous.filter((entry) => entry.category !== removed.name),
      );
      setAllEntries((previous) =>
        previous.filter((entry) => entry.category !== removed.name),
      );
    },
    [managedCategories],
  );

  return useMemo(
    () => ({
      status,
      entries,
      allEntries,
      categories,
      managedCategories,
      error,
      nextCursor,
      reload,
      search,
      searchAll,
      loadMore,
      create,
      update,
      listRevisions,
      restoreRevision,
      remove,
      createCategory,
      updateCategory,
      reorderCategories,
      removeCategory,
    }),
    [
      status,
      entries,
      allEntries,
      categories,
      managedCategories,
      error,
      nextCursor,
      reload,
      search,
      searchAll,
      loadMore,
      create,
      update,
      listRevisions,
      restoreRevision,
      remove,
      createCategory,
      updateCategory,
      reorderCategories,
      removeCategory,
    ],
  );
}

export function JournalEntriesProvider({
  children,
  deferInitialLoad = false,
}: {
  children: ReactNode;
  deferInitialLoad?: boolean;
}) {
  const value = useJournalEntriesProvider(deferInitialLoad);
  return (
    <JournalEntriesContext.Provider value={value}>
      {children}
    </JournalEntriesContext.Provider>
  );
}
